import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingBagIcon } from 'lucide-react';
import { Wordmark } from '../brand/Wordmark';
import { useCart } from '../../contexts/CartContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { MenuButton } from './MenuButton';
import { LangSwitch } from './LangSwitch';
import { ShopNowButton } from '../shop/ShopNowButton';

/**
 * Minimal header: logo · language · cart · SHOP NOW · hamburger.
 * On the homepage the hero artwork carries the logo and menu, so this
 * header stays hidden until the hero has scrolled out of view.
 */
export function Header() {
  const {
    totals
  } = useCart();
  const {
    t,
    lang
  } = useLanguage();
  const {
    pathname
  } = useLocation();
  const isHome = pathname === '/';
  const [pastHero, setPastHero] = useState(!isHome);
  useEffect(() => {
    if (!isHome) {
      setPastHero(true);
      return;
    }
    setPastHero(false);
    const hero = document.getElementById('home-hero');
    if (!hero) return;
    const io = new IntersectionObserver(([entry]) => setPastHero(!entry.isIntersecting), {
      rootMargin: '-72px 0px 0px 0px'
    });
    io.observe(hero);
    return () => io.disconnect();
  }, [isHome]);
  const visible = !isHome || pastHero;
  const position = isHome ? `fixed inset-x-0 top-0 transition-[transform,visibility] duration-300 ease-[cubic-bezier(0.23,1,0.32,1)] ${visible ? 'visible translate-y-0' : 'invisible -translate-y-full'}` : 'sticky top-0';
  return <header className={`${position} z-40`} data-id="element-436" data-mp="3p3e57uqugitu">
      <div className="border-b mf-hairline bg-deep/90 backdrop-blur-md" data-id="element-437" data-mp="3u04th5csykqc">
        <div className="mx-auto flex h-[72px] max-w-site items-center justify-between gap-4 px-5 md:px-8" data-id="element-438" data-mp="1wlq8xpe5z6x">
          <Link to="/" aria-label="MarineForceLED home" className="shrink-0" data-id="element-439" data-mp="19afe8r86swx4">
            <Wordmark data-id="element-440" data-mp="19ci1x4lvadyo" />
          </Link>
          <div className="flex items-center gap-2 sm:gap-3" data-id="element-441" data-mp="1daza7rh0xuho">
            <nav aria-label="Customer service" className="mr-2 hidden items-center gap-6 lg:flex" data-id="element-442" data-mp="3acd9f7ks7pu4">
              <Link to="/#reviews" className="whitespace-nowrap text-[13px] font-medium uppercase tracking-[0.1em] text-titanium hover:text-white" data-id="element-443" data-mp="35daggli2ta2i">
                {lang === 'es' ? 'Reseñas' : 'Reviews'}
              </Link>
              <Link to="/order-tracking" className="whitespace-nowrap text-[13px] font-medium uppercase tracking-[0.1em] text-titanium hover:text-white" data-id="element-444" data-mp="3edgm5l7j95r4">
                {t('nav.track')}
              </Link>
              <Link to="/support" className="whitespace-nowrap text-[13px] font-medium uppercase tracking-[0.1em] text-titanium hover:text-white" data-id="element-445" data-mp="2mnlpt4l9cfsy">
                {t('nav.support')}
              </Link>
            </nav>
            <LangSwitch className="hidden sm:flex" data-id="element-446" data-mp="2vmpaue3zj5a3" />
            <Link to="/cart" aria-label={`Cart, ${totals.lights} lights`} className="relative flex h-11 w-11 items-center justify-center rounded-md text-titanium transition-colors duration-150 hover:bg-white/5 hover:text-white" data-id="element-447" data-mp="27xms12xm76e0">
              <ShoppingBagIcon className="h-5 w-5" data-id="element-448" data-mp="iey3k01wkkhz" />
              {totals.lights > 0 && <span className="absolute right-0 top-0 flex h-5 min-w-5 items-center justify-center rounded-full bg-marine px-1 text-[11px] font-semibold text-white" data-id="element-449" data-mp="n9c0w804l1pa">
                  {totals.lights}
                </span>}
            </Link>
            <ShopNowButton size="sm" source="header" showPrice className="hidden md:inline-flex" data-id="element-450" data-mp="12631ulpg6lh0" />
            <MenuButton data-id="element-451" data-mp="165241yjso44r" />
          </div>
        </div>
      </div>
    </header>;
}